import { useEffect, useMemo, useRef, useState } from "react";
import EmailEditor from "react-email-editor";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { BaseForm, FormInput, FormTextarea } from "components/BaseForm";
import { buildUnlayerOptions } from "helpers/unlayerOptions";
import { getUnlayerTheme } from "helpers/unlayerTheme";
import {
  useSaveEmailAttachment,
  useUnlayerMergeData,
} from "hooks/useEmailAttachment";
import Attachment from "services/Attachment";
import { doRequest } from "services/helpers";

const MAX_EDITOR_RETRIES = 50; // 50 * 100ms = 5 seconds

const schema = z.object({
  name: z.string().trim().min(1, "Attachment name is required"),
  file_name: z
    .string()
    .trim()
    .min(1, "File name is required (this will be the PDF output name)"),
  description: z.string().trim().min(1, "Description is required"),
});

const fetchPdfBlob = async (url, name) => {
  const path = `/export/download_file?url=${encodeURIComponent(url)}&name=${encodeURIComponent(name)}&mimetype=application/pdf`;
  const response = await doRequest("GET", path, {
    responseType: "blob",
    returnResponseObj: true,
  });
  return new Blob([response.data], { type: "application/pdf" });
};

const EditJsonDialog = ({
  open,
  jsonText,
  error,
  onOpen,
  onChange,
  onApply,
  onCancel,
}) => (
  <>
    <Button type="button" variant="outline" onClick={onOpen}>
      Edit JSON
    </Button>
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="tw:sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit JSON</DialogTitle>
        </DialogHeader>
        <Textarea
          aria-label="Design JSON"
          value={jsonText}
          onChange={e => onChange(e.target.value)}
          rows={30}
          className="tw:font-mono tw:text-xs"
        />
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Action Failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={onApply}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);

const EmailAttachmentGraphicalForm = ({
  attachmentId = null,
  isClone = false,
  initialData = {},
  onSuccess,
  onCancel,
}) => {
  const { mutateAsync: saveAttachment } = useSaveEmailAttachment();
  const { mergeTags, specialLinks } = useUnlayerMergeData();
  const isEdit = !!attachmentId && !isClone;

  const [editorReady, setEditorReady] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [jsonOpen, setJsonOpen] = useState(false);

  const emailEditorRef = useRef(null);
  const editorRetries = useRef(0);
  const isMountedRef = useRef(true);

  const design = initialData.html_content?.design;

  const defaultValues = useMemo(
    () => ({
      // A clone starts unnamed so the user is forced to pick a distinct name.
      name: isClone ? "" : (initialData.name ?? ""),
      file_name: initialData.file_name ?? "",
      description: initialData.description ?? "",
    }),
    [initialData, isClone]
  );

  const options = useMemo(
    () => ({ mergeTags, specialLinks, ...buildUnlayerOptions() }),
    [mergeTags, specialLinks]
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Sync Unlayer theme when app theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      emailEditorRef.current?.editor?.setAppearance({
        theme: getUnlayerTheme(),
      });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const handleEditorLoad = () => {
    const editor = emailEditorRef.current?.editor;
    if (editor) {
      if (design) {
        editor.loadDesign(design);
      }
      setEditorReady(true);
    } else if (editorRetries.current < MAX_EDITOR_RETRIES) {
      editorRetries.current += 1;
      setTimeout(() => {
        if (isMountedRef.current) handleEditorLoad();
      }, 100);
    }
  };

  const withEditor = run =>
    new Promise((resolve, reject) => {
      const editor = emailEditorRef.current?.editor;
      if (!editorReady || !editor) {
        reject(new Error("Editor not ready"));
        return;
      }
      run(editor, resolve, reject);
    });

  const exportDesign = () =>
    withEditor((editor, resolve) => editor.exportHtml(resolve));

  const exportPdf = () =>
    withEditor((editor, resolve, reject) =>
      editor.exportPdf(data => {
        if (!data?.url) {
          reject(new Error("PDF export did not return a URL"));
          return;
        }
        resolve(data.url);
      })
    );

  const handleJsonOpen = () => {
    emailEditorRef.current?.editor?.saveDesign(currentDesign => {
      setJsonText(JSON.stringify(currentDesign, null, 2));
      setJsonError(null);
      setJsonOpen(true);
    });
  };

  const handleJsonApply = () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setJsonError("Invalid JSON — please check the format and try again.");
      return;
    }
    setJsonError(null);
    emailEditorRef.current?.editor?.loadDesign(parsed);
    setJsonOpen(false);
  };

  const handleJsonCancel = () => {
    setJsonError(null);
    setJsonOpen(false);
  };

  const handleSubmit = async values => {
    const fileName = values.file_name.trim();
    const fullFileName = fileName.toLowerCase().endsWith(".pdf")
      ? fileName
      : `${fileName}.pdf`;

    const pdfUrl = await exportPdf();
    const pdfBlob = await fetchPdfBlob(pdfUrl, fullFileName);
    const { design: exportedDesign, html } = await exportDesign();
    const pdfFile = new File([pdfBlob], fullFileName, {
      type: "application/pdf",
    });
    const s3Attachment = await Attachment.uploadFile(pdfFile, () => {});

    await saveAttachment({
      id: isEdit ? attachmentId : null,
      data: {
        name: values.name.trim(),
        file_name: fullFileName,
        description: values.description.trim(),
        html_content: { design: exportedDesign, html },
        attachments: [s3Attachment],
      },
    });
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      onCancel={onCancel}
      submitButtonText={isEdit ? "Save" : "Create"}
      buttonsLeft={
        <EditJsonDialog
          open={jsonOpen}
          jsonText={jsonText}
          error={jsonError}
          onOpen={handleJsonOpen}
          onChange={setJsonText}
          onApply={handleJsonApply}
          onCancel={handleJsonCancel}
        />
      }
    >
      <Card>
        <CardContent>
          <div className="tw:flex tw:flex-col tw:gap-5">
            <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
              <FormInput
                name="name"
                label="Attachment Name"
                placeholder="Enter attachment name"
                required
              />
              <FormInput
                name="file_name"
                label="File Name (PDF output)"
                placeholder="e.g. escalation-letter.pdf"
                required
              />
            </div>
            <FormTextarea
              name="description"
              label="Description"
              placeholder="Enter description"
              rows={2}
              required
            />
            <Separator />
            <div className="tw:min-h-[60vh]">
              <EmailEditor
                minHeight="60vh"
                ref={emailEditorRef}
                options={options}
                onLoad={handleEditorLoad}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </BaseForm>
  );
};

export default EmailAttachmentGraphicalForm;
